import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { toast } from 'sonner';
import { 
  Radar, 
  MapPin, 
  Settings, 
  Play, 
  Clock, 
  Globe,
  Plus,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  Activity,
  Users,
  Building2,
  BarChart3,
  Plane,
  Ship,
  Church,
  Eye,
  SlidersHorizontal,
  Sparkles,
  Calendar,
  X,
  Check
} from 'lucide-react';

export default function IntelligenceSettings() {
  const { token, viewingAsUser, isViewingAsOther, effectiveUser } = useAuth();
  const { get, put, post, del } = useApi();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState({ cities: [] });
  const [sweepSettings, setSweepSettings] = useState({
    frequency: 'daily',
    hour: 2,
    minute: 0,
    enabled: true,
    excluded_industries: [],
    custom_industries: []
  });
  const [sweepLogs, setSweepLogs] = useState([]);
  const [addCityOpen, setAddCityOpen] = useState(false);
  const [newCity, setNewCity] = useState({ city: '', province: '', country: 'Canada' });

  // Custom industries — May 2026. Users can add up to 10 verticals not in
  // the canonical list. These are persisted alongside excluded_industries
  // via the same PUT /api/intelligence/sweep-settings endpoint.
  const [newCustomName, setNewCustomName] = useState('');
  const MAX_CUSTOM_INDUSTRIES = 10;

  // AI keyword suggestions per custom industry id:
  //   { [customId]: { loading, suggestions:[], remaining_today, error } }
  const [keywordSuggestions, setKeywordSuggestions] = useState({});

  // Phase 4 — Filter Sensitivity
  const [filterSensitivity, setFilterSensitivity] = useState('balanced');
  const [savedFilterSensitivity, setSavedFilterSensitivity] = useState('balanced');
  const [savingSensitivity, setSavingSensitivity] = useState(false);

  // Document 2 · Phase 1 — Engine Health Badge deep-link support
  const [searchParams] = useSearchParams();
  const VALID_TABS = ['coverage', 'schedule', 'logs', 'filter-sensitivity'];
  const requestedTab = searchParams.get('tab');
  const initialTab = VALID_TABS.includes(requestedTab) ? requestedTab : 'coverage';
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && VALID_TABS.includes(t)) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Phase 5 — Sweep Cycle Recommendation
  const [sweepRec, setSweepRec] = useState(null);
  const [sessionDismissed, setSessionDismissed] = useState(() => {
    try { return sessionStorage.getItem('sweepRecDismissed') === '1'; } catch { return false; }
  });
  const [applyingRec, setApplyingRec] = useState(false);
  const [permanentlyDismissing, setPermanentlyDismissing] = useState(false);

  // Industries with default ON/OFF states
  const AVAILABLE_INDUSTRIES = [
    // Hospitality & Travel
    { id: 'hotels', name: 'Hotels & Hospitality', icon: Plane, iconElement: '🏨', defaultOn: true, category: 'hospitality' },
    { id: 'restaurants', name: 'Restaurants & Food Service', icon: null, iconElement: '🍽️', defaultOn: true, category: 'hospitality' },
    { id: 'airlines', name: 'Airlines', icon: Plane, iconElement: null, defaultOn: false, category: 'hospitality' },
    { id: 'cruise_lines', name: 'Cruise Lines', icon: Ship, iconElement: null, defaultOn: false, category: 'hospitality' },
    
    // B2B Services
    { id: 'software_saas', name: 'Software / SaaS', icon: null, iconElement: '💻', defaultOn: false, category: 'b2b' },
    { id: 'consulting', name: 'Consulting / Professional Services', icon: null, iconElement: '📊', defaultOn: false, category: 'b2b' },
    { id: 'marketing_agencies', name: 'Marketing & Advertising Agencies', icon: null, iconElement: '📣', defaultOn: false, category: 'b2b' },
    { id: 'real_estate', name: 'Real Estate Brokerages', icon: null, iconElement: '🏢', defaultOn: false, category: 'b2b' },
    { id: 'tech_startups', name: 'Technology Startups', icon: null, iconElement: '🚀', defaultOn: false, category: 'b2b' },
    { id: 'b2b_services', name: 'B2B Services Companies', icon: null, iconElement: '🤝', defaultOn: false, category: 'b2b' },
    { id: 'recruiting_staffing', name: 'Recruiting / Staffing Firms', icon: null, iconElement: '👥', defaultOn: false, category: 'b2b' },
    
    // Apparel & Uniform Services
    { id: 'corporate_apparel', name: 'Corporate Apparel Providers', icon: null, iconElement: '👔', defaultOn: false, category: 'apparel' },
    { id: 'uniform_rental', name: 'Uniform Rental Companies', icon: null, iconElement: '🔄', defaultOn: false, category: 'apparel' },
    { id: 'workwear_suppliers', name: 'Workwear Suppliers', icon: null, iconElement: '🦺', defaultOn: false, category: 'apparel' },
    { id: 'promotional_apparel', name: 'Promotional Apparel Companies', icon: null, iconElement: '🎽', defaultOn: false, category: 'apparel' },
    { id: 'hospitality_apparel', name: 'Hospitality Apparel Providers', icon: null, iconElement: '🏨', defaultOn: false, category: 'apparel' },
    { id: 'safety_workwear', name: 'Safety / Industrial Workwear', icon: null, iconElement: '⛑️', defaultOn: false, category: 'apparel' },
    { id: 'facility_services', name: 'Facility Services Companies', icon: null, iconElement: '🏢', defaultOn: false, category: 'apparel' },
    
    // Other Industries
    { id: 'funeral_services', name: 'Funeral Services', icon: Church, iconElement: null, defaultOn: false, category: 'other' },
    { id: 'healthcare', name: 'Healthcare & Medical', icon: null, iconElement: '🏥', defaultOn: false, category: 'other' },
    { id: 'retail', name: 'Retail & Fashion', icon: null, iconElement: '🛍️', defaultOn: false, category: 'other' },
    { id: 'auto_dealerships', name: 'Auto Dealerships', icon: null, iconElement: '🚗', defaultOn: false, category: 'other' },
    { id: 'education_training', name: 'Education & Training', icon: null, iconElement: '🎓', defaultOn: false, category: 'other' },
    { id: 'nonprofit_associations', name: 'Non-profit & Associations', icon: null, iconElement: '🤲', defaultOn: false, category: 'other' },

    // Professional Services (added May 2026 — additive only)
    { id: 'legal_services', name: 'Legal Services / Law Firms', icon: null, iconElement: '⚖️', defaultOn: false, category: 'professional' },
    { id: 'financial_accounting', name: 'Financial Services / Accounting', icon: null, iconElement: '📈', defaultOn: false, category: 'professional' },
    { id: 'insurance_brokerages', name: 'Insurance Brokerages', icon: null, iconElement: '🛡️', defaultOn: false, category: 'professional' },

    // Trades & Construction (added May 2026)
    { id: 'general_contractors', name: 'General Contractors', icon: null, iconElement: '🏗️', defaultOn: false, category: 'trades' },
    { id: 'skilled_trades', name: 'Trades & Skilled Labour', icon: null, iconElement: '🔧', defaultOn: false, category: 'trades' },
    { id: 'property_management', name: 'Property Management Companies', icon: null, iconElement: '🏘️', defaultOn: false, category: 'trades' },

    // Logistics & Operations (added May 2026)
    { id: 'logistics_transport', name: 'Logistics & Transportation', icon: null, iconElement: '🚚', defaultOn: false, category: 'logistics' },
    { id: 'manufacturing', name: 'Manufacturing Companies', icon: null, iconElement: '🏭', defaultOn: false, category: 'logistics' },
    { id: 'warehousing_distribution', name: 'Warehousing & Distribution', icon: null, iconElement: '📦', defaultOn: false, category: 'logistics' }
  ];

  // Get default excluded industries
  const getDefaultExcludedIndustries = () => {
    return AVAILABLE_INDUSTRIES.filter(i => !i.defaultOn).map(i => i.id);
  };

  useEffect(() => {
    fetchAll();
  }, [viewingAsUser]);

  async function fetchAll() {
    setLoading(true);
    try {
      const [statsData, coverageData, settingsData, logsData, sensitivityData, sweepRecData] = await Promise.all([
        get('/intelligence/stats'),
        get('/intelligence/market-coverage'),
        get('/intelligence/sweep-settings'),
        get('/intelligence/sweep-logs?limit=5'),
        get('/signal-drafts/filter-sensitivity').catch(() => null),
        get('/signal-drafts/sweep-recommendation').catch(() => null),
      ]);
      
      if (statsData) setStats(statsData);
      if (coverageData) setCoverage(coverageData);
      if (settingsData) setSweepSettings(settingsData);
      if (logsData) setSweepLogs(logsData);
      if (sensitivityData?.setting) {
        setFilterSensitivity(sensitivityData.setting);
        setSavedFilterSensitivity(sensitivityData.setting);
      }
      if (sweepRecData) setSweepRec(sweepRecData);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  }

  async function applySweepRecommendation() {
    if (!sweepRec) return;
    setApplyingRec(true);
    try {
      // Reuse existing PUT /intelligence/sweep-settings — only frequency changes
      const updated = { ...sweepSettings, frequency: sweepRec.recommended_frequency };
      await put('/intelligence/sweep-settings', updated);
      setSweepSettings(updated);
      // Re-fetch recommendation so card updates to "matches" state
      const fresh = await get('/signal-drafts/sweep-recommendation');
      setSweepRec(fresh);
      toast.success(`Sweep frequency set to ${sweepRec.recommended_frequency.toUpperCase()}`);
    } catch (err) {
      toast.error(err.message || 'Failed to apply recommendation');
    } finally {
      setApplyingRec(false);
    }
  }

  function dismissSweepRecSession() {
    try { sessionStorage.setItem('sweepRecDismissed', '1'); } catch {}
    setSessionDismissed(true);
    toast.info('Recommendation hidden for this session');
  }

  async function dismissSweepRecPermanent() {
    setPermanentlyDismissing(true);
    try {
      await post('/signal-drafts/sweep-recommendation/dismiss', { permanent: true });
      const fresh = await get('/signal-drafts/sweep-recommendation');
      setSweepRec(fresh);
      toast.success(`Recommendation hidden until you change Detection Mode`);
    } catch (err) {
      toast.error(err.message || 'Failed to dismiss recommendation');
    } finally {
      setPermanentlyDismissing(false);
    }
  }

  async function saveFilterSensitivity() {
    setSavingSensitivity(true);
    try {
      await put('/signal-drafts/filter-sensitivity', { setting: filterSensitivity });
      setSavedFilterSensitivity(filterSensitivity);
      toast.success(`Filter sensitivity saved: ${filterSensitivity.toUpperCase()}. Affects future Generate Now runs only.`);
    } catch (err) {
      toast.error(err.message || 'Failed to save filter sensitivity');
    } finally {
      setSavingSensitivity(false);
    }
  }

  async function handleRunSweep() {
    setRunning(true);
    try {
      const result = await post('/intelligence/run-sweep');
      
      if (result) {
        if (result.status === 'started') {
          toast.success('Intelligence sweep started! Check sweep logs for progress.');
          // Poll for completion
          setTimeout(() => {
            setRunning(false);
            fetchAll();
          }, 5000);
        } else {
          toast.success(`Sweep complete: ${result.signals_created} signals, ${result.prospects_created} prospects`);
          setRunning(false);
          fetchAll();
        }
      } else {
        toast.error('Failed to run sweep');
        setRunning(false);
      }
    } catch (err) {
      toast.error('Error running sweep');
      setRunning(false);
    }
  }

  async function handleUpdateSettings() {
    try {
      const result = await put('/intelligence/sweep-settings', sweepSettings);
      
      if (result) {
        toast.success('Settings updated');
      }
    } catch (err) {
      toast.error('Error updating settings');
    }
  }

  // ============ Custom Industries (May 2026) ============
  function handleAddCustomIndustry() {
    const trimmed = newCustomName.trim();
    if (!trimmed) {
      toast.error('Industry name is required');
      return;
    }
    if (trimmed.length > 60) {
      toast.error('Name must be 60 characters or fewer');
      return;
    }
    const existing = sweepSettings.custom_industries || [];
    if (existing.length >= MAX_CUSTOM_INDUSTRIES) {
      toast.error(`Maximum ${MAX_CUSTOM_INDUSTRIES} custom industries`);
      return;
    }
    // Generate stable id from name; collision-check against existing ids.
    const baseSlug = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    let id = `custom_${baseSlug || 'industry'}`;
    let n = 2;
    const existingIds = new Set(existing.map(c => c.id));
    while (existingIds.has(id)) {
      id = `custom_${baseSlug}_${n++}`;
    }
    setSweepSettings(prev => ({
      ...prev,
      custom_industries: [...(prev.custom_industries || []), { id, name: trimmed }]
    }));
    setNewCustomName('');
    toast.success(`Added "${trimmed}" — click Save Schedule Settings to persist`);
  }

  function handleRemoveCustomIndustry(id) {
    setSweepSettings(prev => ({
      ...prev,
      custom_industries: (prev.custom_industries || []).filter(c => c.id !== id),
      // If user had excluded it, also drop the id from excluded_industries
      // so we don't leave orphaned references in the DB.
      excluded_industries: (prev.excluded_industries || []).filter(eid => eid !== id),
    }));
    // Also clear any in-flight or shown suggestions for this id
    setKeywordSuggestions(prev => {
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
    toast.success('Custom industry removed — click Save Schedule Settings to persist');
  }

  // ============ AI keyword suggestions for custom industries ============
  async function handleSuggestKeywords(customId, industryName) {
    setKeywordSuggestions(prev => ({
      ...prev,
      [customId]: { ...(prev[customId] || {}), loading: true, error: null },
    }));
    try {
      const res = await post('/intelligence/custom-industry-keywords', {
        custom_id: customId,
        industry_name: industryName,
      });
      const ci = (sweepSettings.custom_industries || []).find(c => c.id === customId);
      const alreadyAccepted = new Set((ci?.keywords || []).map(k => k.toLowerCase()));
      // Filter out anything already in this industry's accepted keywords
      const fresh = (res?.keywords || []).filter(k => !alreadyAccepted.has(k.toLowerCase()));
      setKeywordSuggestions(prev => ({
        ...prev,
        [customId]: {
          loading: false,
          suggestions: fresh,
          remaining_today: res?.remaining_today,
          rate_limited: !!res?.rate_limited,
          error: res?.ok === false ? (res?.error || 'No suggestions') : null,
        },
      }));
      if (res?.rate_limited) {
        toast.error(res.error || 'Daily limit reached — try again tomorrow');
      } else if (res?.ok === false) {
        toast.error(res?.error || 'Could not generate suggestions');
      } else if (fresh.length === 0) {
        toast.info('All suggestions are already in your accepted list');
      }
    } catch (err) {
      setKeywordSuggestions(prev => ({
        ...prev,
        [customId]: { loading: false, suggestions: [], error: 'Suggestion service is temporarily unavailable.' },
      }));
      toast.error('Suggestion service is temporarily unavailable.');
    }
  }

  function handleAcceptKeyword(customId, keyword) {
    setSweepSettings(prev => ({
      ...prev,
      custom_industries: (prev.custom_industries || []).map(c => {
        if (c.id !== customId) return c;
        const existing = c.keywords || [];
        if (existing.some(k => k.toLowerCase() === keyword.toLowerCase())) return c;
        return { ...c, keywords: [...existing, keyword] };
      }),
    }));
    // Drop the accepted chip from the suggestion list
    setKeywordSuggestions(prev => ({
      ...prev,
      [customId]: {
        ...(prev[customId] || {}),
        suggestions: (prev[customId]?.suggestions || []).filter(k => k.toLowerCase() !== keyword.toLowerCase()),
      },
    }));
  }

  function handleRejectKeyword(customId, keyword) {
    setKeywordSuggestions(prev => ({
      ...prev,
      [customId]: {
        ...(prev[customId] || {}),
        suggestions: (prev[customId]?.suggestions || []).filter(k => k.toLowerCase() !== keyword.toLowerCase()),
      },
    }));
  }

  function handleRemoveAcceptedKeyword(customId, keyword) {
    setSweepSettings(prev => ({
      ...prev,
      custom_industries: (prev.custom_industries || []).map(c =>
        c.id === customId
          ? { ...c, keywords: (c.keywords || []).filter(k => k.toLowerCase() !== keyword.toLowerCase()) }
          : c
      ),
    }));
  }

  async function handleAddCity() {
    if (!newCity.city.trim()) {
      toast.error('City name is required');
      return;
    }
    
    try {
      const result = await post('/intelligence/market-coverage/add-city', newCity);
      
      if (result) {
        toast.success('City added');
        setAddCityOpen(false);
        setNewCity({ city: '', province: '', country: 'Canada' });
        fetchAll();
      } else {
        toast.error('Failed to add city');
      }
    } catch (err) {
      toast.error(err.message || 'Error adding city');
    }
  }

  async function handleRemoveCity(cityName) {
    try {
      await del(`/intelligence/market-coverage/${encodeURIComponent(cityName)}`);
      toast.success('City removed');
      fetchAll();
    } catch (err) {
      toast.error('Error removing city');
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="intelligence-settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-light text-[#1A1A1A] tracking-tight">Intelligence Settings</h1>
            <p className="text-[#6B7280] mt-1">Configure the Canadian Hospitality Opportunity Radar</p>
          </div>
          <Button 
            onClick={handleRunSweep} 
            disabled={running}
            className="bg-[#C9A227] hover:bg-[#B8922A]"
          >
            {running ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run Intelligence Sweep
          </Button>
        </div>

        {/* View As User Banner */}
        {isViewingAsOther && effectiveUser && (
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-500 text-white">
                <Eye className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium text-amber-900">
                  Configuring settings for: <span className="font-semibold">{effectiveUser.name}</span>
                </p>
                <p className="text-sm text-amber-700">
                  Changes will apply to {effectiveUser.name}'s market coverage and sweep schedule
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-4 text-center">
              <Globe className="w-8 h-8 text-[#C9A227] mx-auto mb-2" />
              <p className="text-2xl font-semibold">{stats?.markets_monitored || 0}</p>
              <p className="text-sm text-[#6B7280]">Markets Monitored</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-4 text-center">
              <Radar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-semibold">{stats?.auto_discovered_signals || 0}</p>
              <p className="text-sm text-[#6B7280]">Auto-Discovered Signals</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-4 text-center">
              <Building2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-semibold">{stats?.auto_discovered_prospects || 0}</p>
              <p className="text-sm text-[#6B7280]">Auto-Discovered Prospects</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-semibold">{stats?.pending_suggested_contacts || 0}</p>
              <p className="text-sm text-[#6B7280]">Suggested Contacts</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[#F3F4F6]">
            <TabsTrigger value="coverage" className="data-[state=active]:bg-white">
              <MapPin className="w-4 h-4 mr-2" />Market Coverage
            </TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-white">
              <Clock className="w-4 h-4 mr-2" />Sweep Schedule
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-white">
              <Activity className="w-4 h-4 mr-2" />Sweep Logs
            </TabsTrigger>
            <TabsTrigger value="filter-sensitivity" className="data-[state=active]:bg-white" data-testid="tab-filter-sensitivity">
              <SlidersHorizontal className="w-4 h-4 mr-2" />Filter Sensitivity
            </TabsTrigger>
          </TabsList>

          {/* Market Coverage Tab */}
          <TabsContent value="coverage">
            <Card className="border-[#E5E7EB]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-medium">Market Coverage</CardTitle>
                  <CardDescription>Cities and regions monitored by the intelligence sweep</CardDescription>
                </div>
                <Button variant="outline" onClick={() => setAddCityOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />Add City
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {coverage.cities?.map((city, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    >
                      <div>
                        <p className="font-medium text-[#1A1A1A]">{city.city}</p>
                        <p className="text-sm text-[#6B7280]">
                          {city.province || city.province_state}, {city.country}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveCity(city.city)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                {coverage.cities?.length === 0 && (
                  <p className="text-center text-[#6B7280] py-8">No cities configured</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sweep Schedule Tab */}
          <TabsContent value="schedule">
            {/* Phase 5 — Sweep Cycle Recommendation card (display-only; user applies manually) */}
            {sweepRec && !sessionDismissed && !sweepRec.permanently_dismissed && (
              <Card
                className={`mb-4 border-2 ${sweepRec.matches ? 'border-green-300 bg-green-50' : 'border-[#B89B5E]/60 bg-gradient-to-r from-[#B89B5E]/5 to-transparent'}`}
                data-testid="sweep-recommendation-card"
              >
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${sweepRec.matches ? 'bg-green-100' : 'bg-[#B89B5E]/15'}`}>
                        {sweepRec.matches ? (
                          <CheckCircle className="h-5 w-5 text-green-700" />
                        ) : (
                          <Sparkles className="h-5 w-5 text-[#B89B5E]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base flex items-center gap-2 flex-wrap">
                          {sweepRec.matches
                            ? 'Your sweep schedule matches the recommendation'
                            : 'Recommended sweep cycle for your setup'}
                          <Badge variant="outline" className="text-xs uppercase">
                            <Calendar className="w-3 h-3 mr-1" /> {sweepRec.detection_mode} mode
                          </Badge>
                        </h3>

                        {sweepRec.matches ? (
                          <p className="text-sm text-stone-700 mt-1">
                            Based on your <strong>{sweepRec.detection_mode.toUpperCase()}</strong> Detection Mode, your current
                            <strong> {sweepRec.current_frequency.toUpperCase()} </strong> schedule is optimal. No action needed.
                          </p>
                        ) : (
                          <>
                            <p className="text-sm text-stone-700 mt-1">
                              Based on your Detection Mode <strong>{sweepRec.detection_mode.toUpperCase()}</strong>:
                            </p>
                            <div className="mt-2 p-3 rounded-md bg-white border border-[#B89B5E]/30">
                              <p className="text-sm">
                                <span className="text-stone-500">Recommended:</span>{' '}
                                <strong className="text-[#B89B5E] uppercase" data-testid="recommended-frequency">{sweepRec.recommended_frequency}</strong> sweeps
                              </p>
                              <p className="text-xs text-stone-600 mt-1">{sweepRec.rationale}</p>
                            </div>
                            <p className="text-xs text-stone-500 mt-2">
                              Currently configured: <strong className="uppercase">{sweepRec.current_frequency}</strong> (does not match recommendation)
                            </p>
                            <p className="text-xs text-stone-500 mt-1 italic">
                              This is a suggestion only. Existing sweep continues on its current schedule until you click Apply.
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions row */}
                  {!sweepRec.matches && (
                    <div className="flex items-center gap-2 mt-4 flex-wrap justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={dismissSweepRecPermanent}
                        disabled={permanentlyDismissing}
                        data-testid="dismiss-permanent-btn"
                        className="text-stone-500"
                      >
                        {permanentlyDismissing ? (
                          <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</>
                        ) : (
                          <>Don't show again</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={dismissSweepRecSession}
                        data-testid="dismiss-session-btn"
                      >
                        Dismiss
                      </Button>
                      <Button
                        size="sm"
                        onClick={applySweepRecommendation}
                        disabled={applyingRec}
                        className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                        data-testid="apply-sweep-recommendation-btn"
                      >
                        {applyingRec ? (
                          <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Applying...</>
                        ) : (
                          <><CheckCircle className="h-4 w-4 mr-1" /> Apply Recommendation</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="border-[#E5E7EB]">
              <CardHeader>
                <CardTitle className="text-lg font-medium">Sweep Schedule</CardTitle>
                <CardDescription>Configure automatic intelligence sweep timing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Enable Automated Sweeps</Label>
                    <p className="text-sm text-[#6B7280]">Automatically scan for opportunities</p>
                  </div>
                  <Switch 
                    checked={sweepSettings.enabled} 
                    onCheckedChange={(v) => setSweepSettings(prev => ({ ...prev, enabled: v }))}
                    data-testid="automated-sweeps-toggle"
                  />
                </div>

                {/* Live proof the scheduler is real — shows the most recent
                    AUTOMATIC run (distinct from manual button clicks) so
                    users can confirm the toggle does something. Pulls from
                    /intelligence/stats: `last_auto_sweep`, `next_auto_sweep_at`. */}
                <div
                  className="rounded-sm border border-[#E5E7EB] bg-[#FAF8F5] px-4 py-3 text-sm"
                  data-testid="auto-sweep-status"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-xs uppercase tracking-wider text-[#6B7280]">
                      Last automatic sweep
                    </span>
                    <span className="font-medium text-[#1C1C1C]" data-testid="last-auto-sweep-time">
                      {stats?.last_auto_sweep
                        ? new Date(stats.last_auto_sweep).toLocaleString(undefined, {
                            month: 'short', day: 'numeric',
                            hour: 'numeric', minute: '2-digit',
                          })
                        : 'Not yet — first run will happen within the next 5 minutes'}
                    </span>
                  </div>
                  {stats?.last_auto_sweep && (
                    <div className="flex items-center justify-between flex-wrap gap-2 mt-2 pt-2 border-t border-[#E5E7EB]/60">
                      <span className="text-xs uppercase tracking-wider text-[#6B7280]">
                        {stats.last_auto_sweep_status === 'failed' ? 'Status' : 'Signals created'}
                      </span>
                      <span
                        className="font-medium"
                        style={{ color: stats.last_auto_sweep_status === 'failed' ? '#B23A48' : '#B89B5E' }}
                      >
                        {stats.last_auto_sweep_status === 'failed'
                          ? 'Failed — will retry in ~1 hour'
                          : (stats.last_auto_sweep_signals_created ?? 0)}
                      </span>
                    </div>
                  )}
                  {stats?.next_auto_sweep_at && (
                    <div className="flex items-center justify-between flex-wrap gap-2 mt-2 pt-2 border-t border-[#E5E7EB]/60">
                      <span className="text-xs uppercase tracking-wider text-[#6B7280]">
                        Next scheduled run
                      </span>
                      <span className="text-[#1C1C1C]" data-testid="next-auto-sweep-time">
                        {new Date(stats.next_auto_sweep_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select 
                      value={sweepSettings.frequency} 
                      onValueChange={(v) => setSweepSettings(prev => ({ ...prev, frequency: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="twice_daily">Twice Daily</SelectItem>
                        <SelectItem value="every_6_hours">Every 6 Hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Start Time (Server Time)</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="number" 
                        min="0" 
                        max="23"
                        value={sweepSettings.hour}
                        onChange={(e) => setSweepSettings(prev => ({ ...prev, hour: parseInt(e.target.value) || 0 }))}
                        className="w-20"
                        placeholder="Hour"
                      />
                      <span className="self-center">:</span>
                      <Input 
                        type="number" 
                        min="0" 
                        max="59"
                        value={sweepSettings.minute}
                        onChange={(e) => setSweepSettings(prev => ({ ...prev, minute: parseInt(e.target.value) || 0 }))}
                        className="w-20"
                        placeholder="Min"
                      />
                    </div>
                  </div>
                </div>

                {/* Industry Filter Section */}
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <Label className="text-base">Industry Focus</Label>
                    <p className="text-sm text-[#6B7280]">Select which industries to include in opportunity scanning</p>
                  </div>

                  {/* Multi-Industry Mode — May 2026 */}
                  <div
                    data-testid="multi-industry-mode-row"
                    className={`flex items-start justify-between gap-4 p-3 border rounded-lg ${
                      sweepSettings.multi_industry_mode ? 'border-[#C9A227] bg-[#FFFEF5]' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Sparkles className={`w-4 h-4 ${sweepSettings.multi_industry_mode ? 'text-[#C9A227]' : 'text-gray-400'}`} />
                        <span className="text-sm font-semibold text-[#1A1A1A]">Multi-Industry Mode</span>
                      </div>
                      <p className="text-xs text-[#6B7280] mt-1">
                        Run every enabled industry's queries in one sweep (instead of letting the AI pick one based on your seller profile). Best for sellers serving multiple verticals.
                      </p>
                    </div>
                    <Switch
                      checked={!!sweepSettings.multi_industry_mode}
                      onCheckedChange={(checked) => {
                        setSweepSettings(prev => ({ ...prev, multi_industry_mode: !!checked }));
                      }}
                      data-testid="multi-industry-mode-switch"
                    />
                  </div>

                  {/* Hospitality & Travel */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Hospitality & Travel</h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
                      {AVAILABLE_INDUSTRIES.filter(i => i.category === 'hospitality').map((industry) => {
                        const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                        const IconComponent = industry.icon;
                        return (
                          <div 
                            key={industry.id}
                            className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                              isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                            }`}
                            onClick={() => {
                              setSweepSettings(prev => ({
                                ...prev,
                                excluded_industries: isExcluded 
                                  ? prev.excluded_industries.filter(i => i !== industry.id)
                                  : [...(prev.excluded_industries || []), industry.id]
                              }));
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${isExcluded ? 'text-gray-400' : 'text-[#C9A227]'}`} />
                              ) : (
                                <span className="text-base">{industry.iconElement}</span>
                              )}
                              <span className={`text-xs font-medium ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}>
                                {industry.name}
                              </span>
                            </div>
                            <Switch 
                              checked={!isExcluded}
                              onCheckedChange={() => {
                                setSweepSettings(prev => ({
                                  ...prev,
                                  excluded_industries: isExcluded 
                                    ? prev.excluded_industries.filter(i => i !== industry.id)
                                    : [...(prev.excluded_industries || []), industry.id]
                                }));
                              }}
                              className="scale-75"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* B2B Services */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">B2B Services</h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {AVAILABLE_INDUSTRIES.filter(i => i.category === 'b2b').map((industry) => {
                        const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                        const IconComponent = industry.icon;
                        return (
                          <div 
                            key={industry.id}
                            className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                              isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                            }`}
                            onClick={() => {
                              setSweepSettings(prev => ({
                                ...prev,
                                excluded_industries: isExcluded 
                                  ? prev.excluded_industries.filter(i => i !== industry.id)
                                  : [...(prev.excluded_industries || []), industry.id]
                              }));
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${isExcluded ? 'text-gray-400' : 'text-[#C9A227]'}`} />
                              ) : (
                                <span className="text-base">{industry.iconElement}</span>
                              )}
                              <span className={`text-xs font-medium ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}>
                                {industry.name}
                              </span>
                            </div>
                            <Switch 
                              checked={!isExcluded}
                              onCheckedChange={() => {
                                setSweepSettings(prev => ({
                                  ...prev,
                                  excluded_industries: isExcluded 
                                    ? prev.excluded_industries.filter(i => i !== industry.id)
                                    : [...(prev.excluded_industries || []), industry.id]
                                }));
                              }}
                              className="scale-75"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Apparel & Uniform Services */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Apparel & Uniform Services</h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {AVAILABLE_INDUSTRIES.filter(i => i.category === 'apparel').map((industry) => {
                        const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                        const IconComponent = industry.icon;
                        return (
                          <div 
                            key={industry.id}
                            className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                              isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                            }`}
                            onClick={() => {
                              setSweepSettings(prev => ({
                                ...prev,
                                excluded_industries: isExcluded 
                                  ? prev.excluded_industries.filter(i => i !== industry.id)
                                  : [...(prev.excluded_industries || []), industry.id]
                              }));
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${isExcluded ? 'text-gray-400' : 'text-[#C9A227]'}`} />
                              ) : (
                                <span className="text-base">{industry.iconElement}</span>
                              )}
                              <span className={`text-xs font-medium ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}>
                                {industry.name}
                              </span>
                            </div>
                            <Switch 
                              checked={!isExcluded}
                              onCheckedChange={() => {
                                setSweepSettings(prev => ({
                                  ...prev,
                                  excluded_industries: isExcluded 
                                    ? prev.excluded_industries.filter(i => i !== industry.id)
                                    : [...(prev.excluded_industries || []), industry.id]
                                }));
                              }}
                              className="scale-75"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Professional Services (added May 2026) */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Professional Services</h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {AVAILABLE_INDUSTRIES.filter(i => i.category === 'professional').map((industry) => {
                        const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                        const IconComponent = industry.icon;
                        return (
                          <div
                            key={industry.id}
                            data-testid={`industry-toggle-${industry.id}`}
                            className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                              isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                            }`}
                            onClick={() => {
                              setSweepSettings(prev => ({
                                ...prev,
                                excluded_industries: isExcluded
                                  ? prev.excluded_industries.filter(i => i !== industry.id)
                                  : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                              }));
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${isExcluded ? 'text-gray-400' : 'text-[#C9A227]'}`} />
                              ) : (
                                <span className="text-base">{industry.iconElement}</span>
                              )}
                              <span className={`text-xs font-medium ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}>
                                {industry.name}
                              </span>
                            </div>
                            <Switch
                              checked={!isExcluded}
                              onCheckedChange={() => {
                                setSweepSettings(prev => ({
                                  ...prev,
                                  excluded_industries: isExcluded
                                    ? prev.excluded_industries.filter(i => i !== industry.id)
                                    : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                                }));
                              }}
                              className="scale-75"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Trades & Construction (added May 2026) */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Trades & Construction</h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {AVAILABLE_INDUSTRIES.filter(i => i.category === 'trades').map((industry) => {
                        const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                        const IconComponent = industry.icon;
                        return (
                          <div
                            key={industry.id}
                            data-testid={`industry-toggle-${industry.id}`}
                            className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                              isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                            }`}
                            onClick={() => {
                              setSweepSettings(prev => ({
                                ...prev,
                                excluded_industries: isExcluded
                                  ? prev.excluded_industries.filter(i => i !== industry.id)
                                  : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                              }));
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${isExcluded ? 'text-gray-400' : 'text-[#C9A227]'}`} />
                              ) : (
                                <span className="text-base">{industry.iconElement}</span>
                              )}
                              <span className={`text-xs font-medium ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}>
                                {industry.name}
                              </span>
                            </div>
                            <Switch
                              checked={!isExcluded}
                              onCheckedChange={() => {
                                setSweepSettings(prev => ({
                                  ...prev,
                                  excluded_industries: isExcluded
                                    ? prev.excluded_industries.filter(i => i !== industry.id)
                                    : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                                }));
                              }}
                              className="scale-75"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Logistics & Operations (added May 2026) */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Logistics & Operations</h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {AVAILABLE_INDUSTRIES.filter(i => i.category === 'logistics').map((industry) => {
                        const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                        const IconComponent = industry.icon;
                        return (
                          <div
                            key={industry.id}
                            data-testid={`industry-toggle-${industry.id}`}
                            className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                              isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                            }`}
                            onClick={() => {
                              setSweepSettings(prev => ({
                                ...prev,
                                excluded_industries: isExcluded
                                  ? prev.excluded_industries.filter(i => i !== industry.id)
                                  : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                              }));
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${isExcluded ? 'text-gray-400' : 'text-[#C9A227]'}`} />
                              ) : (
                                <span className="text-base">{industry.iconElement}</span>
                              )}
                              <span className={`text-xs font-medium ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}>
                                {industry.name}
                              </span>
                            </div>
                            <Switch
                              checked={!isExcluded}
                              onCheckedChange={() => {
                                setSweepSettings(prev => ({
                                  ...prev,
                                  excluded_industries: isExcluded
                                    ? prev.excluded_industries.filter(i => i !== industry.id)
                                    : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                                }));
                              }}
                              className="scale-75"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Other Industries */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Other Industries</h4>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {AVAILABLE_INDUSTRIES.filter(i => i.category === 'other').map((industry) => {
                        const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                        const IconComponent = industry.icon;
                        return (
                          <div 
                            key={industry.id}
                            className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                              isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                            }`}
                            onClick={() => {
                              setSweepSettings(prev => ({
                                ...prev,
                                excluded_industries: isExcluded 
                                  ? prev.excluded_industries.filter(i => i !== industry.id)
                                  : [...(prev.excluded_industries || []), industry.id]
                              }));
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {IconComponent ? (
                                <IconComponent className={`w-4 h-4 ${isExcluded ? 'text-gray-400' : 'text-[#C9A227]'}`} />
                              ) : (
                                <span className="text-base">{industry.iconElement}</span>
                              )}
                              <span className={`text-xs font-medium ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}>
                                {industry.name}
                              </span>
                            </div>
                            <Switch 
                              checked={!isExcluded}
                              onCheckedChange={() => {
                                setSweepSettings(prev => ({
                                  ...prev,
                                  excluded_industries: isExcluded 
                                    ? prev.excluded_industries.filter(i => i !== industry.id)
                                    : [...(prev.excluded_industries || []), industry.id]
                                }));
                              }}
                              className="scale-75"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {sweepSettings.excluded_industries?.length > 0 && (
                    <p className="text-xs text-[#6B7280]" data-testid="excluded-industries-summary">
                      Excluding: {Array.from(new Set(sweepSettings.excluded_industries)).map(id => {
                        // Look up in canonical list first, then customs.
                        const canonical = AVAILABLE_INDUSTRIES.find(i => i.id === id)?.name;
                        if (canonical) return canonical;
                        const custom = (sweepSettings.custom_industries || []).find(c => c.id === id)?.name;
                        return custom || id;
                      }).join(', ')}
                    </p>
                  )}

                  {/* Custom Industries — May 2026. Same excluded_industries flow. */}
                  <div className="space-y-2 pt-3 border-t border-dashed">
                    <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide flex items-center gap-2">
                      <span>Custom Industries</span>
                      <span className="text-[10px] font-normal text-[#9CA3AF] normal-case tracking-normal">
                        {(sweepSettings.custom_industries?.length || 0)}/{MAX_CUSTOM_INDUSTRIES} used
                      </span>
                    </h4>
                    {(sweepSettings.custom_industries?.length || 0) > 0 && (
                      <div className="space-y-3">
                        {(sweepSettings.custom_industries || []).map((industry) => {
                          const isExcluded = sweepSettings.excluded_industries?.includes(industry.id);
                          const sug = keywordSuggestions[industry.id] || {};
                          const acceptedKeywords = industry.keywords || [];
                          return (
                            <div key={industry.id} className="space-y-2">
                              <div
                                data-testid={`custom-industry-toggle-${industry.id}`}
                                className={`flex items-center justify-between p-2.5 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                                  isExcluded ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#C9A227] bg-[#FFFEF5]'
                                }`}
                                onClick={(e) => {
                                  if (e.target.closest('[data-testid^="remove-custom-industry-"]')) return;
                                  if (e.target.closest('[data-testid^="suggest-keywords-"]')) return;
                                  setSweepSettings(prev => ({
                                    ...prev,
                                    excluded_industries: isExcluded
                                      ? prev.excluded_industries.filter(i => i !== industry.id)
                                      : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                                  }));
                                }}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span className="text-base flex-shrink-0">✏️</span>
                                  <span
                                    className={`text-xs font-medium truncate ${isExcluded ? 'text-gray-400' : 'text-[#1A1A1A]'}`}
                                    title={industry.name}
                                  >
                                    {industry.name}
                                  </span>
                                  {acceptedKeywords.length > 0 && (
                                    <span className="text-[10px] text-[#8A6F2E] bg-[#FAF4E6] px-1.5 py-0.5 rounded-sm">
                                      {acceptedKeywords.length} kw
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleSuggestKeywords(industry.id, industry.name); }}
                                    disabled={sug.loading || isExcluded}
                                    className="h-7 px-2 text-xs text-[#8A6F2E] hover:bg-[#FAF4E6]"
                                    title={isExcluded ? 'Re-enable this industry to suggest keywords' : 'Suggest 5-8 Canadian B2B search queries (max 3/day)'}
                                    data-testid={`suggest-keywords-${industry.id}`}
                                  >
                                    {sug.loading ? (
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    ) : (
                                      <Sparkles className="w-3 h-3 mr-1" />
                                    )}
                                    Suggest keywords
                                  </Button>
                                  <Switch
                                    checked={!isExcluded}
                                    onCheckedChange={() => {
                                      setSweepSettings(prev => ({
                                        ...prev,
                                        excluded_industries: isExcluded
                                          ? prev.excluded_industries.filter(i => i !== industry.id)
                                          : Array.from(new Set([...(prev.excluded_industries || []), industry.id]))
                                      }));
                                    }}
                                    className="scale-75"
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveCustomIndustry(industry.id); }}
                                    data-testid={`remove-custom-industry-${industry.id}`}
                                    title="Remove this custom industry"
                                    className="text-[#9CA3AF] hover:text-red-600 p-1 rounded-sm hover:bg-red-50 transition-colors"
                                    aria-label={`Remove ${industry.name}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>

                              {/* Suggested keyword chips — dismissible */}
                              {sug.suggestions && sug.suggestions.length > 0 && (
                                <div
                                  className="pl-8 pr-2 flex flex-wrap gap-1.5"
                                  data-testid={`keyword-suggestions-${industry.id}`}
                                >
                                  <span className="text-[10px] text-[#6B7280] self-center mr-1">
                                    Suggestions ({sug.remaining_today != null ? `${sug.remaining_today} left today` : ''}):
                                  </span>
                                  {sug.suggestions.map((kw) => (
                                    <div
                                      key={kw}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-[#D4C49A] rounded-full text-xs"
                                      data-testid={`keyword-suggestion-chip-${industry.id}-${kw.replace(/[^a-z0-9]+/gi, '_')}`}
                                    >
                                      <span className="text-[#1A1A1A]">{kw}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleAcceptKeyword(industry.id, kw)}
                                        className="text-green-700 hover:bg-green-50 rounded-full p-0.5"
                                        title="Accept — adds to this industry's sweep keywords"
                                        data-testid={`accept-keyword-${industry.id}`}
                                      >
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRejectKeyword(industry.id, kw)}
                                        className="text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 rounded-full p-0.5"
                                        title="Reject — dismiss this suggestion"
                                        data-testid={`reject-keyword-${industry.id}`}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Accepted keywords already stored on this industry */}
                              {acceptedKeywords.length > 0 && (
                                <div
                                  className="pl-8 pr-2 flex flex-wrap gap-1.5"
                                  data-testid={`accepted-keywords-${industry.id}`}
                                >
                                  <span className="text-[10px] text-[#6B7280] self-center mr-1">In next sweep:</span>
                                  {acceptedKeywords.map((kw) => (
                                    <div
                                      key={kw}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#FAF4E6] border border-[#B89B5E] rounded-full text-xs"
                                    >
                                      <span className="text-[#8A6F2E]">{kw}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveAcceptedKeyword(industry.id, kw)}
                                        className="text-[#8A6F2E] hover:text-red-600 rounded-full p-0.5"
                                        title="Remove from sweep keywords"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <Input
                        type="text"
                        value={newCustomName}
                        onChange={(e) => setNewCustomName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleAddCustomIndustry(); }
                        }}
                        placeholder="e.g. Yoga Studios, Wedding Venues..."
                        maxLength={60}
                        disabled={(sweepSettings.custom_industries?.length || 0) >= MAX_CUSTOM_INDUSTRIES}
                        className="text-sm flex-1"
                        data-testid="custom-industry-name-input"
                      />
                      <Button
                        type="button"
                        onClick={handleAddCustomIndustry}
                        disabled={!newCustomName.trim() || (sweepSettings.custom_industries?.length || 0) >= MAX_CUSTOM_INDUSTRIES}
                        size="sm"
                        className="bg-[#1A1A1A] hover:bg-[#333] text-white text-xs"
                        data-testid="add-custom-industry-btn"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add
                      </Button>
                    </div>
                    {(sweepSettings.custom_industries?.length || 0) >= MAX_CUSTOM_INDUSTRIES && (
                      <p className="text-xs text-amber-700">
                        You've reached the limit of {MAX_CUSTOM_INDUSTRIES} custom industries. Remove one to add another.
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={handleUpdateSettings} className="bg-[#1A1A1A] hover:bg-[#333]">
                    Save Schedule Settings
                  </Button>
                </div>

                {sweepSettings.last_run && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-[#6B7280]">
                      Last sweep: {new Date(sweepSettings.last_run).toLocaleString()}
                      {' - '}
                      <Badge variant={sweepSettings.last_run_status === 'completed' ? 'default' : 'destructive'}>
                        {sweepSettings.last_run_status}
                      </Badge>
                      {' - '}
                      {sweepSettings.last_run_signals_created} signals created
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sweep Logs Tab */}
          <TabsContent value="logs">
            <Card className="border-[#E5E7EB]">
              <CardHeader>
                <CardTitle className="text-lg font-medium">Recent Sweep Logs</CardTitle>
                <CardDescription>History of intelligence sweep runs</CardDescription>
              </CardHeader>
              <CardContent>
                {sweepLogs.length === 0 ? (
                  <p className="text-center text-[#6B7280] py-8">No sweeps run yet</p>
                ) : (
                  <div className="space-y-3">
                    {sweepLogs.map((log, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {log.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500" />
                            )}
                            <span className="font-medium">{log.status}</span>
                          </div>
                          <span className="text-sm text-[#6B7280]">
                            {new Date(log.started_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-[#6B7280]">Signals Created</p>
                            <p className="font-medium">{log.signals_created}</p>
                          </div>
                          <div>
                            <p className="text-[#6B7280]">Signals Updated</p>
                            <p className="font-medium">{log.signals_updated}</p>
                          </div>
                          <div>
                            <p className="text-[#6B7280]">Prospects Created</p>
                            <p className="font-medium">{log.prospects_created}</p>
                          </div>
                          <div>
                            <p className="text-[#6B7280]">Duration</p>
                            <p className="font-medium">{log.duration_seconds?.toFixed(1)}s</p>
                          </div>
                        </div>
                        {log.sources_scanned?.length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-[#6B7280]">
                              Sources: {log.sources_scanned.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Phase 4 — Filter Sensitivity Tab */}
          <TabsContent value="filter-sensitivity">
            <Card className="border-[#E5E7EB]">
              <CardHeader>
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5 text-[#B89B5E]" />
                  Phase 4 — Filter Sensitivity
                </CardTitle>
                <CardDescription>
                  Controls how strictly the Industry Architect engine filters signals before they appear as drafts. Affects future Generate Now runs only — already-drafted signals are not retroactively re-evaluated.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  {
                    id: 'strict',
                    title: 'Strict',
                    threshold: 0.75,
                    description: 'Highest quality only — fewer signals, more actionable',
                    detail: 'Only signals with strong source authority + clear keyword/segment match + strong city alignment make it through. Best for established users who know their ICP cold.',
                  },
                  {
                    id: 'balanced',
                    title: 'Balanced',
                    threshold: 0.50,
                    description: 'Recommended — quality and volume in balance',
                    detail: 'Default setting. Filters out obvious noise but still surfaces edge-case opportunities for review. Best starting point for most users.',
                  },
                  {
                    id: 'loose',
                    title: 'Loose',
                    threshold: 0.40,
                    description: 'Broader discovery — more signals, some may need more research',
                    detail: 'Maximum signal volume. Good for early adoption when learning what converts, or when entering new markets where ICP is still being calibrated.',
                  },
                ].map((opt) => {
                  const isSelected = filterSensitivity === opt.id;
                  const isCurrentlySaved = savedFilterSensitivity === opt.id;
                  return (
                    <Card
                      key={opt.id}
                      className={`cursor-pointer transition-all border-2 ${
                        isSelected
                          ? 'border-[#B89B5E] bg-[#B89B5E]/5 shadow-sm'
                          : 'border-[#E5E7EB] hover:border-[#B89B5E]/40'
                      }`}
                      onClick={() => setFilterSensitivity(opt.id)}
                      data-testid={`sensitivity-card-${opt.id}`}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'border-[#B89B5E] bg-[#B89B5E]' : 'border-[#D1D5DB]'
                          }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-base">{opt.title}</h3>
                              <Badge variant="outline" className="text-xs font-mono">
                                threshold {opt.threshold.toFixed(2)}
                              </Badge>
                              {opt.id === 'balanced' && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  Default
                                </Badge>
                              )}
                              {isCurrentlySaved && (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Currently saved
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-stone-600 mt-1">{opt.description}</p>
                            <p className="text-xs text-stone-500 mt-2">{opt.detail}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                  <p className="text-xs text-stone-500">
                    Changes apply to <strong>future</strong> Generate Now runs. Already-drafted signals are not re-evaluated.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setFilterSensitivity(savedFilterSensitivity)}
                      disabled={filterSensitivity === savedFilterSensitivity}
                      data-testid="reset-sensitivity-btn"
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Reset
                    </Button>
                    <Button
                      onClick={saveFilterSensitivity}
                      disabled={filterSensitivity === savedFilterSensitivity || savingSensitivity}
                      className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                      data-testid="save-sensitivity-btn"
                    >
                      {savingSensitivity ? (
                        <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</>
                      ) : (
                        <><CheckCircle className="h-4 w-4 mr-1" /> Save Sensitivity</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add City Dialog */}
        <Dialog open={addCityOpen} onOpenChange={setAddCityOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Market City</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>City Name *</Label>
                <Input 
                  value={newCity.city}
                  onChange={(e) => setNewCity(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="e.g., Winnipeg"
                />
              </div>
              <div className="space-y-2">
                <Label>Province / State</Label>
                <Input 
                  value={newCity.province}
                  onChange={(e) => setNewCity(prev => ({ ...prev, province: e.target.value }))}
                  placeholder="e.g., Manitoba"
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input 
                  value={newCity.country}
                  onChange={(e) => setNewCity(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Canada"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setAddCityOpen(false)}>Cancel</Button>
                <Button onClick={handleAddCity} className="bg-[#C9A227] hover:bg-[#B8922A]">Add City</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
